import { color } from "@styles";
import { ContinentBar } from "components/molecules/GlobalRewardsBar/ContinentBar";
import GlobalRewardsDropdown from "components/molecules/GlobalRewardsDropdown";
import GlobalRewardsModal from "components/molecules/GlobalRewardsModal";
import { MAX_MAP_HEIGHT, MAX_MAP_WIDTH } from "components/pages/GlobalRewards/constants";
import { tablet } from "components/templates/Breakpoints";
import React, { FC, RefObject } from "react";
import styled from "styled-components";
import dynamic from "next/dynamic";
import { CountryDetails, Reward, RewardCountry } from "components/pages/GlobalRewards/types";

const MapComponent = dynamic(() => import("components/pages/GlobalRewards/map"), {
  ssr: false,
  loading: () => <p>Loading map...</p>,
});

interface RewardsMapDesktop {
  mapRef: RefObject<any>;
  selectedContinent: string | null;
  selectedCountry: string | null;
  handleCountryClick: (countryId: string) => void;
  handleContinentClick: (continent: string) => void;
  rewardCountries: RewardCountry[];
  mapDimensions: { width: number; height: number };
  isModalOpen: boolean;
  countryRewards: Reward[];
  countryDetails: CountryDetails;
  closeModal: () => void;
  resetCountryModalOnChange?: () => void;
}

export const RewardsMapDesktop: FC<RewardsMapDesktop> = ({
  mapRef,
  selectedContinent,
  selectedCountry,
  handleCountryClick,
  handleContinentClick,
  rewardCountries,
  mapDimensions,
  isModalOpen,
  countryRewards,
  countryDetails,
  closeModal,
  resetCountryModalOnChange,
}) => {
  return (
    <MapContainer ref={mapRef}>
      <MapComponent
        selectedContinent={selectedContinent}
        selectedCountry={selectedCountry}
        onCountryClick={handleCountryClick}
        onContinentClick={handleContinentClick}
        rewardCountries={rewardCountries}
        isModalOpen={isModalOpen}
        closeModal={closeModal}
      />
      <DropDownContainer
        // In-line styling is more performant than using styled components, in this case
        style={{
          transform: `scale(${
            mapDimensions
              ? Math.min(mapDimensions.width / MAX_MAP_WIDTH, mapDimensions.height / MAX_MAP_HEIGHT)
              : 1
          })`,
        }}
      >
        <GlobalRewardsDropdown
          countries={rewardCountries}
          mapDimensions={mapDimensions}
          onCountryClick={handleCountryClick}
          onContinentClick={handleContinentClick}
          selectedCountry={selectedCountry}
          resetCountryModalOnChange={resetCountryModalOnChange}
          closeModal={closeModal}
          isModalOpen={isModalOpen}
        />
      </DropDownContainer>
      <ContinentBar selectedContinent={selectedContinent} onContinentClick={handleContinentClick} />
      {isModalOpen ? (
        <GlobalRewardsModal
          selectedContinent={selectedContinent}
          rewards={countryRewards}
          countryDetails={countryDetails}
          closeModal={closeModal}
          mapDimensions={mapDimensions}
          resetCountryModalOnChange={resetCountryModalOnChange}
        />
      ) : null}
    </MapContainer>
  );
};

export const MapContainer = styled.div`
  margin: 3.75rem auto 2.5rem auto;
  background: ${color.S210};
  width: 90%;
  height: 0;
  padding-bottom: 58%;
  position: relative;
  overflow: hidden;
  max-height: 45rem;
  max-width: 70rem;
  z-index: 1;

  ${tablet} {
    margin-top: 2.5rem;
  }
`;

export const DropDownContainer = styled.div`
  width: 22.375rem;
  position: absolute;
  top: 0;
  left: 0;
  margin: 1.5rem;
  z-index: 2;
  transform-origin: top left;

  ${tablet} {
    margin: 1rem;
  }
`;
